/**
 * Environment Variable Validation
 *
 * Validates that all required environment variables are set before the app starts.
 * This prevents cryptic runtime errors by failing fast with actionable messages.
 */

interface EnvConfig {
  name: string;
  required: boolean;
  description: string;
}

/**
 * Configuration for all environment variables used by the app.
 * Required variables will cause the app to fail if missing.
 * Optional variables will log a warning but allow the app to start.
 */
const ENV_CONFIG: EnvConfig[] = [
  {
    name: 'VITE_SUPABASE_URL',
    required: true,
    description: 'Supabase project URL (Settings > API)',
  },
  {
    name: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    description: 'Supabase anonymous/public key (Settings > API)',
  },
  {
    name: 'VITE_STRIPE_PUBLISHABLE_KEY',
    required: false,
    description: 'Stripe publishable key for payments',
  },
  {
    name: 'VITE_SENTRY_DSN',
    required: false,
    description: 'Sentry DSN for error tracking',
  },
];

export interface ValidationResult {
  isValid: boolean;
  missingRequired: string[];
  missingOptional: string[];
  errors: Array<{ name: string; description: string }>;
}

/**
 * Gets an environment variable value, supporting both Vite and Node environments.
 */
function getEnvVar(key: string): string | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
}

/**
 * Validates all required environment variables are present.
 * Returns a result object with validation status and details.
 */
export function validateEnvironment(): ValidationResult {
  const missingRequired: string[] = [];
  const missingOptional: string[] = [];
  const errors: Array<{ name: string; description: string }> = [];

  for (const config of ENV_CONFIG) {
    const value = getEnvVar(config.name);
    const isMissing = !value || value.trim() === '';

    if (isMissing) {
      if (config.required) {
        missingRequired.push(config.name);
        errors.push({ name: config.name, description: config.description });
      } else {
        missingOptional.push(config.name);
      }
    }
  }

  return {
    isValid: missingRequired.length === 0,
    missingRequired,
    missingOptional,
    errors,
  };
}

/**
 * Creates a styled element with specified tag, styles, and optional text content.
 */
function createStyledElement(
  tag: string,
  styles: Partial<CSSStyleDeclaration>,
  textContent?: string
): HTMLElement {
  const element = document.createElement(tag);
  Object.assign(element.style, styles);
  if (textContent !== undefined) {
    element.textContent = textContent;
  }
  return element;
}

/**
 * Renders an error screen when required environment variables are missing.
 * This provides a clear, actionable message to developers.
 * Uses safe DOM manipulation to prevent XSS vulnerabilities.
 */
export function renderEnvError(result: ValidationResult): void {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  // Clear existing content safely
  rootElement.textContent = '';

  // Main container
  const container = createStyledElement('div', {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    maxWidth: '600px',
    margin: '80px auto',
    padding: '24px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#991b1b',
  });

  // Header
  const header = createStyledElement('h1', {
    margin: '0 0 16px 0',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  });
  const warningIcon = createStyledElement('span', { fontSize: '24px' }, '\u26A0');
  header.appendChild(warningIcon);
  header.appendChild(document.createTextNode(' Missing Environment Variables'));
  container.appendChild(header);

  // Description paragraph
  const description = createStyledElement('p', {
    margin: '0 0 16px 0',
    color: '#7f1d1d',
  });
  description.appendChild(
    document.createTextNode(
      'The application cannot start because required configuration is missing. Please set the following environment variables in your '
    )
  );
  const envFileCode = createStyledElement('code', {
    background: '#fee2e2',
    padding: '2px 6px',
    borderRadius: '4px',
  }, '.env');
  description.appendChild(envFileCode);
  description.appendChild(document.createTextNode(' file:'));
  container.appendChild(description);

  // Error list
  const errorList = createStyledElement('ul', {
    margin: '0 0 16px 0',
    paddingLeft: '20px',
  });

  for (const err of result.errors) {
    const listItem = createStyledElement('li', { marginBottom: '8px' });

    const nameCode = createStyledElement('code', {
      background: '#fee2e2',
      padding: '2px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
    });
    nameCode.textContent = err.name;
    listItem.appendChild(nameCode);

    listItem.appendChild(document.createElement('br'));

    const descSpan = createStyledElement('span', {
      color: '#6b7280',
      fontSize: '14px',
    });
    descSpan.textContent = err.description;
    listItem.appendChild(descSpan);

    errorList.appendChild(listItem);
  }
  container.appendChild(errorList);

  // Help box
  const helpBox = createStyledElement('div', {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '6px',
    padding: '12px',
    color: '#9a3412',
    fontSize: '14px',
  });

  const strong = document.createElement('strong');
  strong.textContent = 'How to fix:';
  helpBox.appendChild(strong);

  const steps = createStyledElement('ol', {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  });

  const stepTexts = [
    ['Copy ', '.env.example', ' to ', '.env'],
    ['Fill in the missing values from your Supabase/Stripe dashboard'],
    ['Restart the development server'],
  ];

  for (const stepParts of stepTexts) {
    const stepItem = document.createElement('li');
    for (const part of stepParts) {
      if (part === '.env.example' || part === '.env') {
        const code = document.createElement('code');
        code.textContent = part;
        stepItem.appendChild(code);
      } else {
        stepItem.appendChild(document.createTextNode(part));
      }
    }
    steps.appendChild(stepItem);
  }
  helpBox.appendChild(steps);
  container.appendChild(helpBox);

  rootElement.appendChild(container);
}
